/* eslint-disable react/no-access-state-in-setstate */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import _isFinite from 'lodash/isFinite';
import DateRange from '../product/DateRange';
import CompactCurrencyFilter from '../product/PricingUtils';

const MIN_QUANTITY = 1;
const MAX_QUANTITY = 100;

class CartItem extends Component {
  constructor(props) {
    super(props);

    this.onRemove = this.onRemove.bind(this);
    this.onUpdateQuantity = this.onUpdateQuantity.bind(this);
    this.onResetInvalidQuantity = this.onResetInvalidQuantity.bind(this);

    const { item } = this.props;
    this.state = {
      quantity: item.contribution.quantity,
      persistedQuantity: item.contribution.quantity,
      // eslint-disable-next-line react/no-unused-state
      inputError: null,
    };
  }

  onUpdateQuantity(event) {
    const originalQuantity = event.target.value;
    const updatedQuantity = parseInt(event.target.value, 10);

    // they entered something other than a number, they may be in the middle of typing something so store it but don't persist
    if (!_isFinite(updatedQuantity)) {
      this.setState({
        quantity: originalQuantity,
      });
      return;
    }

    if (updatedQuantity < MIN_QUANTITY || updatedQuantity > MAX_QUANTITY) {
      this.setState({
        quantity: this.state.persistedQuantity,
        // eslint-disable-next-line react/no-unused-state
        inputError: `Please enter a quantity between ${MIN_QUANTITY} and ${MAX_QUANTITY}.`,
      });
      return;
    }

    // Update the quantity in the cart
    const { item, updateQuantity } = this.props;
    updateQuantity(item.id, updatedQuantity)
      .then(() =>
        this.setState({
          quantity: updatedQuantity,
          persistedQuantity: updatedQuantity,
          // eslint-disable-next-line react/no-unused-state
          inputError: null,
        })
      )
      .catch(error => {
        this.setState({
          quantity: this.state.persistedQuantity,
          // eslint-disable-next-line react/no-unused-state
          inputError: error.response.errorMessage,
        });
      });
  }

  onResetInvalidQuantity() {
    const currentQuantity = parseInt(this.state.quantity, 10);
    if (!_isFinite(currentQuantity)) {
      this.setState({
        quantity: this.state.persistedQuantity,
      });
    }
  }

  onRemove() {
    const { item, remove } = this.props;
    remove(item.id);
  }

  render() {
    const { item } = this.props;

    const showOriginalPrice = item.contribution.price > item.contribution.adjustedPrice;
    const availability = item.availability || {};

    return (
      <div className="row cart-item">
        <span className="close visible-xs-block" onClick={this.onRemove}>
          &times;
        </span>
        <div className="col-xs-3 col-sm-2">
          <a href={item.itemLink} target="_self">
            <img className="img-responsive" src={item.imageLink} alt={item.name} />
          </a>
        </div>
        <div className="col-xs-8 col-xs-no-gutter-left col-sm-4">
          <div className="shop-entity-details">
            <a href={item.itemLink} target="_self" className="entity-name">
              {item.name}
            </a>
            {/* group gift tag */}
            {item.groupGift && (
              <div className="group-gift-tag">
                <span className="label label-primary">Group Gift Contribution</span>
              </div>
            )}
            {/* sku attributes and availability info */}
            <div className="cart-item-attributes">
              {item.skuAttributes.map(skuAttribute => (
                <div key={skuAttribute.label} className="value-label attribute-value">
                  <span>
                    <strong>{skuAttribute.label}:</strong> {skuAttribute.value}
                  </span>
                </div>
              ))}
              {availability.stockStatus && (
                <div className="value-label">
                  <strong>Availability:</strong> {availability.stockStatus}
                </div>
              )}
              {availability.arrivalRange && (
                <div className="value-label">
                  <strong>Estimated Arrival:</strong>{' '}
                  <DateRange dateRange={availability.arrivalRange} />
                </div>
              )}
            </div>
          </div>
          {/* item notes */}
          {item.showExternalWarning && (
            <div className="help-block small">
              Please remember that this item cannot be shipped to you and is redeemable by the
              couple at their convenience.
            </div>
          )}
          {item.isUpdated && (
            <div className="help-block small">
              We have a limited quantity of this item. We&apos;ve adjusted your quantity above to
              reflect the maximum number available.
            </div>
          )}
          {item.deliverySurcharge > 0 && (
            <div className="help-block small">
              Due to its weight or perishable nature, this item has an incremental delivery
              surcharge of {CompactCurrencyFilter(item.deliverySurcharge)}.
            </div>
          )}
          {/* shipping alert */}
          {item.isExpititable && (
            <div className="cart-item-discount">Two-day shipping available</div>
          )}
          {/* discounts */}
          {item.discounts.map(discount => (
            <div key={discount} className="cart-item-discount">
              {discount}
            </div>
          ))}
          {item.showExcludeFromPromo && (
            <div className="cart-item-discount">Excluded from Promo</div>
          )}
        </div>
        <div className="hidden-xs col-sm-2 shop-entity-details">
          <div className="price">
            {CompactCurrencyFilter(item.contribution.adjustedPrice)}&nbsp;
            {showOriginalPrice && (
              <s className="small text-muted">{CompactCurrencyFilter(item.contribution.price)}</s>
            )}
          </div>
        </div>
        <div className="clearfix visible-xs-block" />
        <div className="col-xs-6 col-sm-2 cart-item-quantity">
          {!item.groupGift ? (
            <div className="form-group-inline">
              {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
              <label
                htmlFor={`cart-qty-${item.id}`}
                className="control-label visible-xs-inline-block"
              >
                Quantity:
              </label>
              <div>
                <input
                  id={`cart-qty-${item.id}`}
                  className="form-control input-narrow"
                  type="number"
                  required
                  min="1"
                  max="100"
                  step="1"
                  disabled={item.buyGroupGift || !item.isMutable}
                  // eslint-disable-next-line react/destructuring-assignment
                  value={this.state.quantity}
                  onChange={this.onUpdateQuantity}
                  onBlur={this.onResetInvalidQuantity}
                />
                <a role="button" className="text-large hidden-xs" onClick={this.onRemove}>
                  Remove
                </a>
              </div>
            </div>
          ) : (
            <a role="button" className="text-large hidden-xs" onClick={this.onRemove}>
              Remove
            </a>
          )}
        </div>
        <div className="col-xs-6 col-sm-2 cart-item-subtotal">
          <strong className="text-danger">{CompactCurrencyFilter(item.subtotal)}</strong>
        </div>
      </div>
    );
  }
}

CartItem.propTypes = {
  item: PropTypes.shape({}),
  updateQuantity: PropTypes.func.isRequired,
  remove: PropTypes.func.isRequired,
};

export default CartItem;



// WEBPACK FOOTER //
// ./src/components/cart/CartItem.jsx